import { UIKit } from "@/shared/ui";
/**
 * Виджет «Развёртки стен» — вид на стены помещения изнутри.
 *
 * Ничего дополнительно чертить не нужно: развёртки строятся из плана
 * (`widgets/plan/lib/elevations.ts`), поэтому любая правка чертежа сразу
 * меняет и их. Здесь виджет отвечает за выбор помещения, раскладку листов
 * и за то, чтобы по стене можно было кликнуть и поставить розетку.
 *
 * Управление: колесо — зум, средняя кнопка или Shift+ЛКМ — панорама,
 * перетаскивание точки двигает её по стене, Alt — без привязок.
 */
import { useEffect, useRef, useState, type PointerEvent } from "react";
import {
	buildRoomElevations,
	detectRooms,
	roomFinishedCeilingHeight,
	roomTitle,
} from "@/widgets/plan";
import { MaterialDefs } from "@/widgets/catalog";
import { useProjectStore, Tool } from "@/shared/store";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { RoomMap3D, RoomMapModal3D } from "@/widgets/scene-3d";
import { usePlaceFixture } from "@/widgets/place-fixture";
import { useEditorHotkeys } from "@/widgets/editor-hotkeys";
import { useTouchGestures, useTransformPlan, useViewport } from "@/shared/hooks";
import { layoutElevations, type SheetLayout } from "../layout";
import { ElevationSheet } from "./ElevationSheet";
import { floorItemsStoreFor } from "@/shared/store/layers/floorItemsStore";
import { effectiveWallGraph, type FloorItem, allFixtures, allFloorItems } from "@/shared/plan";

const noop = () => {};
const ELEVATION_INSETS = { left: 66, right: 0 };
const MOBILE_LAYOUT_QUERY = "(max-width: 767px)";

export const WallElevations = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const tool = useEditorStore((s) => s.tool);
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	const selection = useEditorStore((s) => s.selection);
	const showDimensions = useEditorStore((s) => s.showDimensions);
	const setSelection = useEditorStore((s) => s.setSelection);
	const roomId = useEditorStore((s) => s.elevationRoomId);
	const setRoomId = useEditorStore((s) => s.setElevationRoomId);
	const showRoomMap = useEditorStore((s) => s.showElevationRoomMap);
	const toggleRoomMap = useEditorStore((s) => s.toggleElevationRoomMap);
	const overlayTopOffset = useEditorStore((s) => s.overlayTopOffset);
	const insets = ELEVATION_INSETS;
	const [mobile, setMobile] = useState(
		() => typeof window !== "undefined" && window.matchMedia(MOBILE_LAYOUT_QUERY).matches,
	);
	useEffect(() => {
		const query = window.matchMedia(MOBILE_LAYOUT_QUERY);
		const updateMobile = () => setMobile(query.matches);
		updateMobile();
		query.addEventListener("change", updateMobile);
		return () => query.removeEventListener("change", updateMobile);
	}, []);

	const viewport = useViewport({ x: -2000, y: -1000, w: 12000, h: 9000 });
	const { svgRef, box, toWorld, mmPerPx, zoomAt, panByPixels } = viewport;
	const touch = useTouchGestures({ zoomAt, panByPixels });
	const transform = useTransformPlan({ toWorld, panByPixels, mmPerPx });
	const scale = mmPerPx();

	const fixtures = usePlaceFixture();
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const floorDrag = useRef<{
		item: FloorItem;
		sheet: SheetLayout;
		startX: number;
		startY: number;
		startElevation: number;
		height: number;
	} | null>(null);
	useEditorHotkeys({ onCancelDraft: noop });

	const graph = effectiveWallGraph(layers.walls);
	const rooms = detectRooms(graph);
	const room = rooms.find((r) => r.id === roomId) ?? rooms[0] ?? null;
	const roomCeilingHeights = Object.fromEntries(
		rooms.map((currentRoom) => [
			currentRoom.id,
			layers.ceiling.items[currentRoom.id]
				? roomFinishedCeilingHeight(graph, currentRoom, layers.ceiling.items)
				: undefined,
		]),
	);
	const selectRoom = (selectedRoomId: string) => {
		setRoomId(selectedRoomId);
		if (mobile) toggleRoomMap();
	};

	// Развёртка — чертёж стены целиком: на ней все объекты, какие бы слои ни оставил
	// включёнными этап 2D («Сантехника» прячет мебель и электрику, а на развёртке они нужны)
	const layout = room
		? layoutElevations(buildRoomElevations(graph, allFixtures(layers), room, allFloorItems(layers)))
		: null;

	// Вписываем лист в экран при смене помещения
	const fittedFor = useRef<string | null>(null);
	// oxlint-disable-next-line react/exhaustive-deps
	useEffect(() => {
		if (!layout || !room || fittedFor.current === room.id) return;
		fittedFor.current = room.id;
		viewport.fit(layout.bounds, insets);
	}, [layout, room, viewport, insets]);

	/** Экран → координаты конкретной развёртки (x вдоль стены, y от пола). */
	const localFor = (sheet: SheetLayout) => (clientX: number, clientY: number) => {
		const world = toWorld(clientX, clientY);
		return { x: world.x - sheet.x, y: sheet.floorY - world.y };
	};

	const beginFloorItemDrag = (
		e: PointerEvent<SVGGElement>,
		item: FloorItem,
		elevation: number,
		height: number,
		sheet: SheetLayout,
	) => {
		if (isReadOnly || e.button !== 0 || e.shiftKey || tool !== Tool.Select) return;
		e.stopPropagation();
		setSelection({ kind: SelectionKind.Item, id: item.id });
		pushHistory();
		const start = localFor(sheet)(e.clientX, e.clientY);
		floorDrag.current = {
			item,
			sheet,
			startX: start.x,
			startY: start.y,
			startElevation: elevation,
			height,
		};
		try {
			e.currentTarget.setPointerCapture(e.pointerId);
		} catch {
			// Указатель мог быть уже снят браузером.
		}
	};

	if (rooms.length === 0 || !room || !layout) {
		return (
			<div className="absolute inset-0 flex flex-col bg-bg">
				<div className="m-auto max-w-95 rounded-xl border border-border bg-surface px-7 py-6 text-center">
					<UIKit.Text className="mb-2 text-lg font-semibold">Развёрток пока нет</UIKit.Text>
					<UIKit.Text className="text-sm leading-copy-loose text-ink">
						Развёртки строятся по помещениям. Замкните контур стен на чертеже — и стены комнаты
						появятся здесь вместе с проёмами.
					</UIKit.Text>
				</div>
			</div>
		);
	}

	return (
		<div className="absolute inset-0 min-w-0 overflow-hidden bg-bg">
			{rooms.length > 1 && (
				<div
					className="pointer-events-none absolute inset-x-0 z-10 flex justify-center px-2 pl-18 md:pl-20"
					style={{ top: overlayTopOffset }}
				>
					<div
						className="pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-lg border border-border bg-surface/95 p-1 shadow-sm backdrop-blur-sm"
						role="tablist"
						aria-label="Помещения"
					>
						{rooms.map((candidate, index) => {
							const active = candidate.id === room.id;
							return (
								<UIKit.Button
									key={candidate.id}
									type="button"
									border={false}
									role="tab"
									aria-selected={active}
									className={`shrink-0 rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${
										active
											? "bg-accent text-overlay-ink shadow-xs hover:bg-accent"
											: "text-ink hover:bg-surface-muted hover:text-accent"
									}`}
									onClick={() => setRoomId(candidate.id)}
								>
									<UIKit.Text as="span" size="small" className="text-xs font-medium text-current">
										{roomTitle(layers.rooms, candidate.id, index)}
									</UIKit.Text>
								</UIKit.Button>
							);
						})}
					</div>
				</div>
			)}
			<svg
				ref={svgRef}
				className="absolute inset-0 block size-full touch-none bg-bg"
				viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
				onPointerDownCapture={(e) => {
					if (!isReadOnly) return;
					if (touch.handlePointerDown(e)) {
						transform.handlePointerUp(e);
						fixtures.handlePointerUp();
						floorDrag.current = null;
					} else {
						transform.beginPan(e);
					}
					e.stopPropagation();
				}}
				onPointerMove={(e) => {
					if (touch.handlePointerMove(e)) return;
					if (transform.handlePointerMove(e)) return;
					if (isReadOnly) return;
					const floorItemDrag = floorDrag.current;
					if (floorItemDrag) {
						const local = localFor(floorItemDrag.sheet)(e.clientX, e.clientY);
						const distance = local.x - floorItemDrag.startX;
						floorItemsStoreFor(floorItemDrag.item.ref.catalog)
							.getState()
							.updateFloorItem(floorItemDrag.item.id, {
								elevation: Math.max(
									0,
									Math.min(
										floorItemDrag.sheet.elevation.height - floorItemDrag.height,
										floorItemDrag.startElevation + (local.y - floorItemDrag.startY),
									),
								),
								x: floorItemDrag.item.x + floorItemDrag.sheet.elevation.axis.x * distance,
								y: floorItemDrag.item.y + floorItemDrag.sheet.elevation.axis.y * distance,
							});
						return;
					}
					fixtures.handlePointerMove(e);
				}}
				onPointerUp={(e) => {
					touch.handlePointerUp(e);
					transform.handlePointerUp(e);
					floorDrag.current = null;
					fixtures.handlePointerUp();
				}}
				onPointerCancel={(e) => {
					touch.handlePointerUp(e);
					transform.handlePointerUp(e);
					floorDrag.current = null;
					fixtures.handlePointerUp();
				}}
				onPointerLeave={(e) => {
					transform.handlePointerUp(e);
					floorDrag.current = null;
					fixtures.handlePointerUp();
				}}
				onWheel={(e) => zoomAt(e.clientX, e.clientY, e.deltaY > 0 ? 1.12 : 1 / 1.12)}
				onPointerDown={(e) => {
					// Два пальца — панорама и зум, перетаскивание точки при этом бросаем
					if (touch.handlePointerDown(e)) {
						transform.handlePointerUp(e);
						fixtures.handlePointerUp();
						return;
					}
					const role = (e.target as HTMLElement).dataset?.role;
					const missed = role === undefined || (e.pointerType === "touch" && role === "wall-face");
					const dragEmpty = e.button === 0 && missed && tool === Tool.Select;
					const wantsPan =
						e.button === 1 || e.button === 2 || (e.button === 0 && e.shiftKey) || dragEmpty;
					if (wantsPan) {
						if (dragEmpty) setSelection(null);
						transform.beginPan(e);
						return;
					}
					if (missed) setSelection(null);
				}}
			>
				<MaterialDefs mmPerPx={scale} />

				{layout.sheets.map((sheet) => (
					<ElevationSheet
						key={sheet.elevation.wallId}
						sheet={sheet}
						mmPerPx={scale}
						selection={selection}
						interactive
						showDimensions={showDimensions}
						onWallPointerDown={(e) => {
							if (isReadOnly || e.button !== 0) return;
							const local = localFor(sheet)(e.clientX, e.clientY);
							if (fixtures.placeOnElevation(sheet.elevation, local, e.altKey)) {
								e.stopPropagation();
								return;
							}
							if (e.pointerType === "touch") return;
							setSelection({ kind: SelectionKind.Wall, id: sheet.elevation.wallId });
							e.stopPropagation();
						}}
						onFixturePointerDown={(e, fixtureId) => {
							if (isReadOnly || e.button !== 0 || e.shiftKey) return;
							setSelection({ kind: SelectionKind.Fixture, id: fixtureId });
							if (tool !== Tool.Select && tool !== Tool.Place) return;
							fixtures.beginDrag(e, fixtureId, {
								elevation: sheet.elevation,
								toLocal: localFor(sheet),
							});
						}}
						onFloorItemPointerDown={(e, item, elevation, height) =>
							beginFloorItemDrag(e, item, elevation, height, sheet)
						}
					/>
				))}
			</svg>
			{showRoomMap && !mobile && (
				<div className="absolute right-3 bottom-3 z-10">
					<RoomMap3D
						rooms={rooms}
						ceilingHeights={roomCeilingHeights}
						selectedRoomId={room.id}
						onSelectRoom={selectRoom}
						onClose={toggleRoomMap}
					/>
				</div>
			)}
			{showRoomMap && mobile && (
				<RoomMapModal3D
					rooms={rooms}
					ceilingHeights={roomCeilingHeights}
					selectedRoomId={room.id}
					onSelectRoom={selectRoom}
					onClose={toggleRoomMap}
				/>
			)}
		</div>
	);
};
